import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CheckCircle2, PackageSearch, Network, FileSearch, Globe2, FileDown } from "lucide-react";
import { useLang } from "../context/LangContext";
import { IMG, CORE_SERVICES, INDUSTRIES, LIFECYCLE_STEPS, WHY_CHOOSE } from "../lib/siteData";
import { tr } from "../lib/contentTranslations";

const Home = () => {
  const { t, lang } = useLang();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] bg-ink text-white overflow-hidden flex items-center">
        <img src={IMG.heroBg} alt="Industrial facility" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-20 w-full">
          <div className="max-w-3xl">
            <div className="overline text-brand mb-6 animate-fade-up">{t("hero.eyebrow")}</div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight animate-fade-up" data-testid="hero-title">
              {t("hero.title")}
            </h1>
            <p className="mt-7 text-base sm:text-lg text-steel-200 max-w-2xl leading-relaxed animate-fade-up" style={{ animationDelay: "120ms" }}>
              {t("hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: "200ms" }}>
              <Link to="/rfq" data-testid="hero-rfq-cta" className="group inline-flex items-center gap-2 bg-brand hover:bg-brand-hover transition-colors font-semibold text-sm px-7 py-4 uppercase tracking-[0.18em]">
                {t("common.requestQuote")} <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link to="/contact" data-testid="hero-contact-cta" className="group inline-flex items-center gap-2 border-2 border-white hover:bg-white hover:text-ink transition-colors font-semibold text-sm px-7 py-4 uppercase tracking-[0.18em]">
                {t("common.contactUs")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/profile.pdf`}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="hero-download-profile"
                className="group inline-flex items-center gap-2 border-2 border-brand text-white bg-brand/20 hover:bg-brand transition-colors font-semibold text-sm px-7 py-4 uppercase tracking-[0.18em]"
              >
                <FileDown className="w-4 h-4" /> Download Company Profile
              </a>
            </div>
            <div className="mt-12 flex items-center gap-4 text-xs text-steel-400 font-mono">
              <span className="w-12 h-px bg-brand" />
              <span className="uppercase tracking-[0.25em]">{t("hero.tagline")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline mb-4">{t("home.overviewLabel")}</div>
            <h2 className="section-title text-3xl sm:text-4xl lg:text-5xl mb-6">{t("home.overviewTitle")}</h2>
            <p className="text-ink-muted text-base lg:text-lg leading-relaxed">{t("home.overviewBody")}</p>
            <div className="mt-6 inline-flex items-center gap-3 bg-steel-50 border-l-4 border-brand px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-ink-soft">
              <span className="font-mono text-brand">IFZA</span>
              <span className="h-3 w-px bg-steel-300" />
              <span>Registered in Dubai, UAE</span>
            </div>
            <Link to="/about" className="inline-flex items-center gap-2 mt-8 text-brand font-semibold text-sm uppercase tracking-[0.18em] hover:gap-3 transition-all ml-0 sm:ml-6" data-testid="overview-about-cta">
              {t("common.learnMore")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            <img src={IMG.waterTreatment} alt="Water treatment plant" className="w-full h-64 lg:h-80 object-cover" />
            <img src={IMG.roMembranes} alt="RO membranes" className="w-full h-64 lg:h-80 object-cover translate-y-8" />
            <img src={IMG.cartridgeFilters} alt="Cartridge filters" className="w-full h-64 lg:h-80 object-cover bg-ink" />
            <img src={IMG.engineeringReview} alt="Engineering review" className="w-full h-64 lg:h-80 object-cover translate-y-8" />
          </div>
        </div>
      </section>

      {/* CORE SERVICES */}
      <section className="py-20 lg:py-28 bg-steel-50 border-y border-steel-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="overline mb-4">{t("home.servicesLabel")}</div>
              <h2 className="section-title text-3xl sm:text-4xl lg:text-5xl">{t("home.servicesTitle")}</h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-brand font-semibold text-sm uppercase tracking-[0.18em]" data-testid="services-view-all">
              {t("common.viewAll")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-steel-200 border border-steel-200">
            {CORE_SERVICES.map((s, idx) => {
              const Icon = s.icon;
              return (
                <div key={s.key} data-testid={`service-card-${s.key}`} className="indicator-line bg-white p-8 lg:p-10 group transition-all hover:bg-steel-50">
                  <div className="flex items-start justify-between mb-7">
                    <Icon className="w-9 h-9 text-brand" strokeWidth={1.4} />
                    <span className="num-tag">{String(idx + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-ink mb-3 leading-tight">{tr(lang, s.title)}</h3>
                  <p className="text-sm text-ink-muted leading-relaxed">{tr(lang, s.desc)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="overline mb-4">{t("home.industriesLabel")}</div>
            <h2 className="section-title text-3xl sm:text-4xl lg:text-5xl">{t("home.industriesTitle")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {INDUSTRIES.map((ind) => (
              <Link key={ind.key} to="/industries" data-testid={`industry-card-${ind.key}`} className="group relative overflow-hidden block">
                <img src={ind.img} alt={ind.title} className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="font-heading font-bold text-xl mb-2">{tr(lang, ind.title)}</h3>
                  <p className="text-sm text-steel-200 line-clamp-2">{tr(lang, ind.desc)}</p>
                  <div className="mt-3 inline-flex items-center gap-2 text-brand text-xs uppercase tracking-[0.2em] font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                    Explore <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* LIFECYCLE */}
      <section className="py-20 lg:py-28 bg-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url(${IMG.processPlant})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="overline text-brand mb-4">{t("home.lifecycleLabel")}</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight mb-5">{t("home.lifecycleTitle")}</h2>
            <p className="text-steel-300 text-base lg:text-lg leading-relaxed">{t("home.lifecycleBody")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-steel-800 border border-steel-800">
            {LIFECYCLE_STEPS.map((step) => (
              <div key={step.n} className="bg-ink p-8 lg:p-10 group hover:bg-steel-900 transition-colors" data-testid={`lifecycle-step-${step.n}`}>
                <div className="flex items-center gap-4 mb-5">
                  <span className="font-mono text-brand text-sm">{step.n}</span>
                  <span className="h-px flex-1 bg-steel-700" />
                </div>
                <h3 className="font-heading font-bold text-xl mb-3">{tr(lang, step.title)}</h3>
                <p className="text-sm text-steel-400 leading-relaxed">{tr(lang, step.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCUREMENT & VENDOR NETWORK */}
      <section className="py-20 lg:py-28 bg-white border-y border-steel-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-4">{t("home.partnersLabel")}</div>
            <h2 className="section-title text-3xl sm:text-4xl mb-6">{t("home.partnersTitle")}</h2>
            <p className="text-ink-muted text-base lg:text-lg leading-relaxed mb-6">
              We support buyers and project teams across international sourcing, technical bid evaluation, expediting and logistics — a structured procurement workflow tailored to each project. Strategic local partners in Iraq, Egypt and Libya support on-the-ground coordination.
            </p>
            <Link to="/partners" className="inline-flex items-center gap-2 text-brand font-semibold text-sm uppercase tracking-[0.18em] hover:gap-3 transition-all" data-testid="home-partners-cta">
              Meet our local partners <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-steel-200 border border-steel-200">
            {[
              { icon: PackageSearch, title: "International Sourcing", desc: "Vendor identification and supplier qualification across mechanical, electrical and I&C scopes." },
              { icon: FileSearch, title: "Technical Bid Evaluation", desc: "Independent technical and commercial evaluation of vendor offers and documentation." },
              { icon: Network, title: "Vendor Coordination", desc: "Commercial and technical alignment between client and supplier throughout the engagement." },
              { icon: Globe2, title: "Logistics & Expediting", desc: "Inspection coordination, expediting and logistics support from supplier to job-site." },
            ].map((b, i) => (
              <div key={b.title} className="bg-white p-7 indicator-line" data-testid={`procurement-card-${i}`}>
                <b.icon className="w-7 h-7 text-brand mb-4" strokeWidth={1.4} />
                <h3 className="font-heading font-bold text-base text-ink uppercase tracking-wide mb-2">{b.title}</h3>
                <p className="text-sm text-ink-muted leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 lg:py-28 bg-steel-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-4">{t("home.whyLabel")}</div>
            <h2 className="section-title text-3xl sm:text-4xl lg:text-5xl mb-6">{t("home.whyTitle")}</h2>
            <img src={IMG.industrialFacility} alt="Industrial site" className="w-full h-80 object-cover mt-6" />
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-steel-200 border border-steel-200">
            {WHY_CHOOSE.map((w, idx) => (
              <div key={w.title} className="bg-white p-7 indicator-line" data-testid={`why-${idx}`}>
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-brand" strokeWidth={1.5} />
                  <h3 className="font-heading font-bold text-base text-ink uppercase tracking-wide">{tr(lang, w.title)}</h3>
                </div>
                <p className="text-sm text-ink-muted leading-relaxed">{tr(lang, w.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand text-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight mb-4">{t("home.ctaTitle")}</h2>
            <p className="text-white/85 text-base lg:text-lg max-w-2xl">{t("home.ctaBody")}</p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
            <Link to="/rfq" data-testid="cta-rfq" className="inline-flex items-center justify-center gap-2 bg-white text-brand hover:bg-ink hover:text-white transition-colors font-semibold text-sm px-7 py-4 uppercase tracking-[0.18em]">
              {t("common.submitRfq")} <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" data-testid="cta-contact" className="inline-flex items-center justify-center gap-2 border-2 border-white hover:bg-white hover:text-brand transition-colors font-semibold text-sm px-7 py-4 uppercase tracking-[0.18em]">
              {t("common.contactTeam")} <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/profile.pdf`}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="cta-download-profile"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/50 hover:bg-white hover:text-brand transition-colors font-semibold text-sm px-7 py-4 uppercase tracking-[0.18em]"
            >
              <FileDown className="w-4 h-4" /> Company Profile (PDF)
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
